type GoogleTokenResponse = {
    access_token?: string;
    error?: string;
    error_description?: string;
};

type TokenClient = {
    requestAccessToken: (options?: { prompt?: string }) => void;
};

type GoogleAccounts = {
    oauth2: {
        initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: GoogleTokenResponse) => void;
        }) => TokenClient;
    };
};

declare global {
    interface Window {
        google?: {
            accounts: GoogleAccounts;
        };
    }
}

const GOOGLE_IDENTITY_SCRIPT_ID = 'google-identity-services';
const GOOGLE_IDENTITY_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

const loadGoogleIdentityScript = (): Promise<void> => {
    if (window.google?.accounts?.oauth2) {
        return Promise.resolve();
    }

    const existingScript = document.getElementById(GOOGLE_IDENTITY_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
        return new Promise((resolve, reject) => {
            existingScript.addEventListener('load', () => resolve(), { once: true });
            existingScript.addEventListener('error', () => reject(new Error('Failed to load Google sign-in')), { once: true });
        });
    }

    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = GOOGLE_IDENTITY_SCRIPT_ID;
        script.src = GOOGLE_IDENTITY_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Google sign-in'));
        document.head.appendChild(script);
    });
};

export const requestGoogleAccessToken = async (): Promise<string> => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

    if (!clientId) {
        throw new Error('Google login is not configured');
    }

    await loadGoogleIdentityScript();

    if (!window.google?.accounts?.oauth2) {
        throw new Error('Google sign-in is unavailable');
    }

    return new Promise((resolve, reject) => {
        const tokenClient = window.google!.accounts.oauth2.initTokenClient({
            client_id: clientId,
            scope: 'openid email profile',
            callback: (response) => {
                if (response.error) {
                    reject(new Error(response.error_description || response.error));
                    return;
                }

                if (!response.access_token) {
                    reject(new Error('Google did not return an access token'));
                    return;
                }

                resolve(response.access_token);
            },
        });

        tokenClient.requestAccessToken({ prompt: 'select_account' });
    });
};
